import React, { useCallback, useRef, useState } from 'react';

import classNames from 'classnames';
import { useCombobox } from 'downshift';

import Label from 'common/ui/common/Label';
import LabelWrapper from 'common/ui/common/select/LabelWrapper';
import OptionPrefix from 'common/ui/common/select/OptionPrefix';
import RightIcons from 'common/ui/common/select/RightIcons';
import SelectOptions from 'common/ui/common/select/SelectOptions';
import SelectPortal from 'common/ui/common/select/SelectPortal';
import SupportingTexts from 'common/ui/common/select/SupportingTexts';
import { sortByGroupBy } from 'common/ui/common/select/util';
import findStringMatches from 'common/util/findStringMatches';

import type { CommonSelectProps, Option } from 'common/ui/common/select/SelectCommon';

import 'css/components/_SelectCommon.scss';

type Props = CommonSelectProps & {
  findMatches?: (options: Option[], search: string) => Option[];
  headClassName?: string;
  onChange: (option?: Option) => void;
  onOpen?: () => void;
  onSearchChange?: (search?: string) => void;
  selectOptionsClassName?: string;
  value?: Option;
  showChevron?: boolean;
};

const SingleSelectWithSearch = ({
  optionsMaxWidth,
  allowClear,
  disabled,
  className,
  cta,
  error,
  findMatches = (options: Option[], search: string) => findStringMatches(options, 'label', search),
  headClassName,
  isItemDisabled = () => false,
  label,
  labelPlacement = 'top',
  loading,
  prefixIcon: PrefixIcon,
  onChange,
  onOpen,
  onSearchChange,
  options,
  optionsPlacement = 'bottom',
  placeholder,
  required = false,
  selectOptionsClassName,
  supportingText,
  value,
  withBorder = true,
  showChevron = true,
  minWidth = 120,
}: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState<string | undefined>(value?.label);
  const sortedOptions = sortByGroupBy(options);
  const filteredOptions =
    searchValue && value?.label !== searchValue
      ? findMatches(sortedOptions, searchValue)
      : sortedOptions;

  const itemToString = useCallback((item: Option | null) => (item ? item.label : ''), []);
  const onSelectedItemChange = useCallback(
    ({ selectedItem: newSelectedItem }) => {
      if (cta && newSelectedItem?.value === cta?.option.value) {
        cta.onAction();
        return;
      }
      onChange(newSelectedItem ?? undefined);
      setSearchValue(newSelectedItem?.label);
    },
    [cta, onChange, setSearchValue],
  );
  const stateReducer = useCallback(
    (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return {
            ...changes,
            inputValue: state.selectedItem?.label,
            selectedItem: state.selectedItem,
          };
        case useCombobox.stateChangeTypes.InputChange:
          if (onSearchChange) {
            onSearchChange(changes.inputValue);
          }
          setSearchValue(changes.inputValue);
          return changes;
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        case useCombobox.stateChangeTypes.InputBlur:
          if (onSearchChange) {
            onSearchChange(value?.label);
          }
          setSearchValue(value?.label);
          return {
            ...changes,
            inputValue: changes.selectedItem?.label,
          };
        default:
          return changes;
      }
    },
    [onSearchChange, value?.label],
  );

  const {
    closeMenu,
    isOpen,
    openMenu,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox<Option>({
    isItemDisabled,
    items: cta ? [...filteredOptions, cta.option] : filteredOptions,
    itemToString,
    onIsOpenChange: ({ isOpen }) => {
      if (isOpen) {
        onOpen?.();
      }
    },
    onSelectedItemChange,
    selectedItem: value || null,
    stateReducer,
  });

  const onClear = useCallback(() => {
    onChange();
    setSearchValue('');
    closeMenu();
  }, [onChange, setSearchValue, closeMenu]);

  return (
    <div className="dropdownV2Wrapper" data-hide-passmgr="true" ref={triggerRef}>
      <LabelWrapper labelPlacement={labelPlacement}>
        {label && <Label {...getLabelProps()} label={label} />}
        <div
          className={classNames('dropdownV2 singleSelect withSearch', className, {
            disabledV2: disabled,
            errorV2: error,
            withBorder,
          })}
          style={{ minWidth }}>
          <div className="dropdownWrapperV2">
            <div
              className={classNames('dropdownHeadWrapper', headClassName)}
              aria-required={required}
              aria-label="toggle menu"
              {...getToggleButtonProps({ disabled })}>
              <div className="leftWrapper">
                {PrefixIcon && <PrefixIcon className="prefixIcon" size={14} />}
                {value?.label && (
                  <OptionPrefix
                    flair={value.flair}
                    avatar={value.avatar}
                    prefixIcon={value.prefixIcon}
                    disabled={disabled}
                  />
                )}
                <input
                  className="selectInput"
                  {...getInputProps({ disabled }, { suppressRefError: true })}
                  onFocus={openMenu}
                  placeholder={value?.label || placeholder}
                  value={searchValue || ''}
                />
              </div>
              <RightIcons
                aria-label="remove selection"
                loading={loading}
                disabled={disabled}
                onClear={onClear}
                showClear={allowClear && !!value}
                error={error}
                showChevron={showChevron}
              />
            </div>
            <SelectPortal
              getMenuProps={getMenuProps}
              optionsMaxWidth={optionsMaxWidth}
              position={optionsPlacement}
              triggerRef={triggerRef}>
              <SelectOptions
                className={selectOptionsClassName}
                ctaOption={cta?.option}
                disabled={disabled}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                isItemDisabled={isItemDisabled}
                isLoading={loading}
                isOpen={isOpen}
                options={filteredOptions}
                optionsMaxWidth={optionsMaxWidth}
                optionsPlacement={optionsPlacement}
                selectedItems={value && [value]}
              />
            </SelectPortal>
          </div>
          <SupportingTexts supportingText={supportingText} error={!disabled ? error : undefined} />
        </div>
      </LabelWrapper>
    </div>
  );
};

export default SingleSelectWithSearch;
